import { getCount, mapItems } from 'components/common/DraggableList/utils';
import { useEffect, useRef, useState } from 'react';
import Draggable, { DraggableEventHandler } from 'react-draggable';

import { Box } from '@project44/manifest-ds';

type TDragHandler = (index: number) => DraggableEventHandler;

type TItems = Array<{
  id: string;
  x: number;
  y: number;
  Component: JSX.Element;
}>;

interface IDraggableList {
  list: Array<{ id: string; Component: JSX.Element }>;
  onChange: (list: string[]) => void;
}

function DraggableList({ list, onChange }: IDraggableList) {
  const [items, setItems] = useState<TItems>(list.map(mapItems));

  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = rootRef?.current?.children as HTMLCollectionOf<HTMLElement>;

  useEffect(() => {
    setItems(list.map(mapItems));
  }, [list]);

  const onDrag: TDragHandler = (index) => (event, ui) => {
    if (!listRef) return;

    const next = [...items];
    const isUp = ui.y < 0;
    const count = getCount(listRef, index, ui.y);
    const pos = index + (isUp ? -count : count);
    const card = listRef[index].offsetHeight;

    setItems(
      next.map((item, idx) => {
        const onUp = idx >= pos && idx <= index ? card : 0;
        const onDown = idx <= pos && idx >= index ? -card : 0;
        return {
          ...item,
          y: isUp ? onUp : onDown,
        };
      }),
    );
  };

  const onStop: TDragHandler = (index) => (event, ui) => {
    if (!listRef) return;
    for (const child of listRef) {
      child.style.zIndex = '0';
      child.style.transitionDuration = '0s';
    }

    if (index > 0 || index < items.length) {
      const count = getCount(listRef, index, ui.y);
      const pos = index + (ui.y < 0 ? -count : count);
      const next = [...items];
      next.splice(index, 1);
      next.splice(pos, 0, items[index]);
      setItems(next.map((item) => ({ ...item, x: 0, y: 0 })));
      onChange(next.map((item) => item.id));
    }
  };

  const onStart: DraggableEventHandler = (event) => {
    if (!listRef) return;
    for (const child of listRef) {
      if (child !== event.currentTarget) {
        child.style.transitionDuration = '0.5s';
      } else {
        child.style.transitionDuration = '0s';
        child.style.zIndex = '1';
      }
    }
  };

  return (
    <Box
      ref={rootRef}
      css={{ backgroundColor: '$background-tertiary', position: 'relative' }}
    >
      {items.map((item, index) => (
        <Draggable
          key={item.id}
          axis="y"
          bounds="parent"
          handle=".dragHandle"
          position={{ x: 0, y: item.y }}
          onDrag={onDrag(index)}
          onStop={onStop(index)}
          onStart={onStart}
        >
          <div
            style={{
              position: 'relative',
            }}
          >
            {item.Component}
          </div>
        </Draggable>
      ))}
    </Box>
  );
}

export default DraggableList;
